/**
 * LeaderboardCard — shared component for all department leaderboards + the
 * CEO Exec Board top-3 summary. Iter-87.
 *
 * Renders: Rank · Name · Badge ID · Score · trend arrow (↑ ↓ →).
 * Accepts `department` (valet/cleaners/sales/customer_service) or `top3Only`
 * mode when embedded on the CEO Executive Home page.
 */
import { useEffect, useState } from 'react';
import { useUnifiedFilter } from '../UnifiedGeoRoleFilter';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Minus, Trophy, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPT_META = {
  valet:            { label: 'Valet Operations',    tone: '#C9A962', icon: '🚛' },
  cleaners:         { label: 'Cleaners Operations', tone: '#22d3ee', icon: '🧽' },
  sales:            { label: 'Sales',               tone: '#10b981', icon: '💼' },
  customer_service: { label: 'Customer Service',    tone: '#0ea5e9', icon: '🎧' },
};

const METRIC_LABELS = {
  routes_completed:      'Routes',
  on_time_rate:          'On-time %',
  compliance_rate:       'Compliance %',
  violation_free_nights: 'Clean Nights',
  leads_captured:        'Leads',
  quotes_sent:           'Quotes',
  contracts_closed:      'Contracts',
  activations_processed: 'Activations',
  tickets_resolved:      'Tickets',
  avg_response_seconds:  'Avg Resp (s)',
};

function TrendArrow({ trend }) {
  if (trend === 'up')   return <TrendingUp   className="h-4 w-4 text-emerald-400" />;
  if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-400" />;
  return <Minus className="h-4 w-4 text-zinc-500" />;
}

export function LeaderboardCard({ department, top3Only = false, title, className = '' }) {
  const boardKey = department === 'customer-service' ? 'customer_service' : department;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter-102 · Item 3 — Unified filter refires the query on state/city
  // change so leaderboard rows scope live with the operator's selection.
  const { filter } = useUnifiedFilter();

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (filter?.state) params.set('state', filter.state);
    if (filter?.city) params.set('city', filter.city);
    fetch(`${API}/api/leaderboards/${boardKey}?${params.toString()}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [boardKey, filter?.state, filter?.city]);

  const meta = DEPT_META[boardKey] || { label: department, tone: '#FFD60A', icon: '🏆' };
  const rows = (data?.rows || []).slice(0, top3Only ? 3 : undefined);
  const isShell = data?.is_shell;

  return (
    <Card className={`bg-zinc-900 border-zinc-700 p-4 ${className}`} data-testid={`leaderboard-${department}`}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h3
          className="text-white font-bold flex items-center gap-2"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          <Trophy className="h-4 w-4" style={{ color: meta.tone }} />
          {title || `${meta.icon} ${meta.label} · Leaderboard`}
          {top3Only && <span className="text-[10px] text-zinc-500">· Top 3</span>}
        </h3>
        <div className="flex items-center gap-2">
          {/* Iter-104 · Item 1 — Coming Soon / Demo Mode badge removed
              across the Cleaners surface. Live boards render empty
              states instead of demo placeholders. */}
          <span className="text-[10px] text-zinc-500">Rolling 7d vs prior 7d</span>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-zinc-500">Loading leaderboard…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid={`leaderboard-${department}-empty`}>No activity in the last 7 days.</p>
      ) : (
        <div className="space-y-1.5" data-testid={`leaderboard-${department}-rows`}>
          {rows.map((r) => (
            <LeaderboardRow key={r.user_id} row={r} tone={meta.tone} isShell={isShell} />
          ))}
        </div>
      )}
    </Card>
  );
}

function LeaderboardRow({ row, tone, isShell }) {
  const rankColor = row.rank === 1 ? '#FFD60A' : row.rank === 2 ? '#C9A962' : row.rank === 3 ? '#a08050' : '#666';
  const metricEntries = Object.entries(row.metrics || {}).slice(0, 3);
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-md border"
      style={{ backgroundColor: '#0a0a0a', borderColor: '#27272a' }}
      data-testid={`leaderboard-row-${row.user_id}`}
    >
      <div
        className="flex-shrink-0 h-8 w-8 rounded-full font-black text-sm flex items-center justify-center"
        style={{ backgroundColor: `${rankColor}22`, color: rankColor, border: `1px solid ${rankColor}80` }}
      >
        {row.rank}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-semibold truncate">
          {row.name} {isShell && <span className="text-[10px] text-cyan-400">(demo)</span>}
        </p>
        <p className="text-[10px] text-zinc-500 truncate">
          #{row.badge_id}
          {metricEntries.map(([k, v]) => (
            <span key={k} className="ml-2">· {METRIC_LABELS[k] || k}: <b style={{ color: '#e6e6e6' }}>{v}</b></span>
          ))}
        </p>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-lg font-black" style={{ color: tone }}>{row.score}</p>
        <div className="flex justify-end"><TrendArrow trend={row.trend} /></div>
      </div>
    </div>
  );
}

/** CEO Exec Board summary — Top 3 per department in a 2×2 grid. */
export function ExecutiveLeaderboardSummary() {
  return (
    <section data-testid="exec-leaderboard-summary">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] font-semibold flex items-center gap-2">
          <Trophy className="h-4 w-4" /> Departmental Leaderboards · Top 3
        </h2>
        <span className="text-[11px] text-zinc-500">Rolling 7d · every department</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <LeaderboardCard department="valet" top3Only />
        <LeaderboardCard department="sales" top3Only />
        <LeaderboardCard department="customer_service" top3Only />
        <LeaderboardCard department="cleaners" top3Only />
      </div>
    </section>
  );
}
